import { createFileRoute } from "@tanstack/react-router";
import { Breadcrumbs, PageHeader, Section, SectionHeading } from "@/components/site/section";
import { CtaLink } from "@/components/site/cta";
import { FinalCTA } from "@/components/site/final-cta";
import { pageUrl } from "@/data/site";

export const Route = createFileRoute("/cal-components")({
  head: () => ({
    meta: [
      { title: ".30 & .50 Cal Pen Components | Paul's Pens n Things" },
      {
        name: "description",
        content:
          "Powder-coated .50 caliber cases and bullets plus once-fired .308, .30-06, .30-30 and .300 Win Mag cartridge assemblies drilled for Cross or Parker refills.",
      },
      { property: "og:title", content: ".30 and .50 caliber pen components" },
      {
        property: "og:description",
        content:
          "Polished, powder-coated cartridge components for penturners — Cross and Parker transmissions installed.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: pageUrl("/cal-components") },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: pageUrl("/cal-components") }],
  }),
  component: CalComponents,
});

type Component = {
  name: string;
  price: string;
  image: string;
  alt: string;
  spec?: string;
};

const fiftyCal: Component[] = [
  {
    name: "50 caliber case",
    price: "$9.95",
    image: "/comp-50cal-case.webp",
    alt: "Powder-coated .50 caliber brass case with the neck expanded for a bullet",
    spec: "Powder coated and with the neck expanded to loosely fit a powder coated bullet.",
  },
  {
    name: "50 caliber bullet — Cross",
    price: "$11.95",
    image: "/comp-50cal-bullet-cross.webp",
    alt: "Powder-coated .50 caliber copper bullet drilled for a Cross refill",
    spec: "Powder coated and drilled for a cross refill.",
  },
  {
    name: "50 caliber bullet — Parker",
    price: "$11.95",
    image: "/comp-50cal-bullet-parker.webp",
    alt: "Powder-coated .50 caliber copper bullet drilled for a Parker refill",
    spec: "Powder coated and drilled for a parker refill.",
  },
];

const crossAssemblies: Component[] = [
  {
    name: "Military version of a 308 (7.62x51)",
    price: "$11.95",
    image: "/comp-762x51-military-308.webp",
    alt: "Powder-coated 7.62x51 military cartridge pen assembly with Cross transmission",
  },
  {
    name: "Military version of a 30-06 (7.62x63)",
    price: "$11.95",
    image: "/comp-762x63-military-3006.webp",
    alt: "Powder-coated 7.62x63 military cartridge pen assembly with Cross transmission",
  },
  {
    name: "Brass 308 Win",
    price: "$11.95",
    image: "/comp-308-brass.webp",
    alt: "Polished brass .308 Winchester cartridge pen assembly",
  },
  {
    name: "Nickle-plated 308 Win",
    price: "$11.95",
    image: "/comp-308-nickel.webp",
    alt: "Nickel-plated once-fired .308 Winchester cartridge pen assembly",
  },
  {
    name: "Nickle-plated 308 Win, new",
    price: "$11.95",
    image: "/comp-308-nickel-new.webp",
    alt: "New nickel-plated .308 Winchester cartridge pen assembly",
  },
  {
    name: "Brass 30-06 Sprg",
    price: "$11.95",
    image: "/comp-3006-brass.webp",
    alt: "Polished brass .30-06 Springfield cartridge pen assembly",
  },
  {
    name: "Nickle-plated 30-06 Sprg",
    price: "$11.95",
    image: "/comp-3006-nickel.webp",
    alt: "Nickel-plated once-fired .30-06 Springfield cartridge pen assembly",
  },
  {
    name: "Nickle-plated 30-06 Sprg, new",
    price: "$11.95",
    image: "/comp-3006-nickel-new.webp",
    alt: "New nickel-plated .30-06 Springfield cartridge pen assembly",
  },
  {
    name: "Brass 30-30",
    price: "$11.95",
    image: "/comp-3030-brass.webp",
    alt: "Polished brass .30-30 cartridge pen assembly",
  },
  {
    name: "Brass 300 Win Mag",
    price: "$11.95",
    image: "/comp-300winmag-brass-cross.webp",
    alt: "Polished brass .300 Winchester Magnum cartridge pen assembly with Cross refill",
  },
  {
    name: "Nickle-plated 300 Win Mag",
    price: "$11.95",
    image: "/comp-300winmag-nickel-cross.webp",
    alt: "Nickel-plated .300 Winchester Magnum cartridge pen assembly with Cross refill",
  },
];

const parkerAssemblies: Component[] = [
  {
    name: "Brass 300 Win Mag",
    price: "$11.95",
    image: "/comp-300winmag-brass-parker.webp",
    alt: "Polished brass .300 Winchester Magnum cartridge pen assembly with Parker adapter",
  },
  {
    name: "Nickle-plated 300 Win Mag",
    price: "$11.95",
    image: "/comp-300winmag-nickel-parker.webp",
    alt: "Nickel-plated .300 Winchester Magnum cartridge pen assembly with Parker adapter",
  },
];

const shipping = "Shipping is $6 for 1 to 5, $11 for 6 to 25, and $19 for 26 or more.";

function ComponentGrid({ items }: { items: Component[] }) {
  return (
    <ul className="mt-12 grid gap-px border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-3">
      {items.map((c) => (
        <li key={`${c.name}-${c.image}`} className="flex flex-col bg-background">
          <div className="overflow-hidden bg-paper">
            <img
              src={c.image}
              alt={c.alt}
              loading="lazy"
              width={360}
              height={271}
              className="h-full w-full object-cover motion-craft transition-transform duration-700 hover:scale-[1.03]"
            />
          </div>
          <div className="flex flex-1 flex-col p-7 sm:p-8">
            <h3 className="font-[family-name:var(--font-display)] text-xl leading-tight">{c.name}</h3>
            <p className="mt-2 text-lg font-semibold text-walnut">{c.price}</p>
            {c.spec && <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.spec}</p>}
            <div className="mt-auto pt-6">
              <CtaLink to="/contact" variant="outline" className="h-11">
                Order this component
              </CtaLink>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function CalComponents() {
  return (
    <>
      <Breadcrumbs
        trail={[
          { label: "Home", to: "/" },
          { label: "For Penturners", to: "/for-pen-turners" },
          { label: ".30 and .50 Cal Components" },
        ]}
      />
      <PageHeader
        eyebrow="Products for penturners"
        title=".30 and .50 cal components."
        lede="Once-fired cases and military bullets, polished, powder-coated and prepped on Paul's bench so the hard part is done before the blank ever touches your lathe."
      >
        <CtaLink to="/contact" variant="outline" className="h-12">
          Ask about availability
        </CtaLink>
      </PageHeader>

      <Section tone="paper" labelledBy="fifty-heading">
        <div className="shell">
          <SectionHeading
            id="fifty-heading"
            eyebrow="50 caliber components"
            title="Powder-coated .50 cal cases and bullets."
            lede={`Powder coated 50 caliber cases and bullets drilled for cross or parker refills. ${shipping}`}
          />
          <ComponentGrid items={fiftyCal} />
        </div>
      </Section>

      <Section labelledBy="cross-heading">
        <div className="shell">
          <SectionHeading
            id="cross-heading"
            eyebrow="Powder-coated cartridge assemblies for Cross refills"
            title="Cross transmission installed."
            lede={`These are made with once-fired rifle catridges and military bullets. They are polished, powder-coated and have a cross transmission installed. ${shipping}`}
          />
          <ComponentGrid items={crossAssemblies} />
        </div>
      </Section>

      <Section tone="paper" labelledBy="parker-heading">
        <div className="shell">
          <SectionHeading
            id="parker-heading"
            eyebrow="Powder-coated cartridge assemblies for Parker refills"
            title="Cigar-kit adapter installed."
            lede={`These are made with once-fired rifle catridges and military bullets. They are polished, powder-coated and have an adapter from a cigar kit installed. ${shipping}`}
          />
          <ComponentGrid items={parkerAssemblies} />
          <p className="mt-8 max-w-2xl text-sm text-muted-foreground">
            Ordering a batch for a club or a build class? Send Paul the counts and he will quote it with the right
            shipping tier applied.
          </p>
        </div>
      </Section>

      <FinalCTA
        heading="Need the build instructions too?"
        body="The free .30 cal and .50 BMG assembly sheets cover every dimension these components are cut to."
        label="Get in touch"
      />
    </>
  );
}
